'use client';

import { useEffect, useRef, useState } from 'react';

import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Crown,
  Gem,
  Gift,
  Shield,
  Sparkles,
  Star,
  Tag,
  X,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent } from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { CheckoutOverlay } from '~/components/billing/checkout-overlay';
import { useDisplayCurrency } from '~/components/currency-provider';
import { trackFunnelEvent } from '~/lib/analytics/track';
import { formatPrice } from '~/lib/currency';

import {
  formatCountdown,
  useFirstPurchaseOffer,
} from './use-first-purchase-offer';

type PackFeature = { type: 'chat' | 'image'; count: number };

// Coin packs matching billing.config.ts
const COIN_PACKS = [
  {
    id: 'coins-700',
    coins: 700,
    price: 4.99,
    originalPrice: 6.99,
    perCoin: '0.007',
    icon: Sparkles,
    gradient: 'from-eros-pink to-rose-400',
    glowColor: 'shadow-eros-pink/20',
    bgAccent: 'bg-eros-pink/10',
    borderAccent: 'border-eros-pink/30',
    features: [
      { type: 'chat', count: 140 },
      { type: 'image', count: 70 },
    ] as PackFeature[],
  },
  {
    id: 'coins-1600',
    coins: 1600,
    price: 9.99,
    originalPrice: 13.99,
    perCoin: '0.006',
    badge: 'POPULAR',
    highlighted: true,
    icon: Crown,
    gradient: 'from-eros-pink to-eros-purple',
    glowColor: 'shadow-eros-pink/30',
    bgAccent: 'bg-eros-pink/15',
    borderAccent: 'border-eros-pink/50',
    features: [
      { type: 'chat', count: 320 },
      { type: 'image', count: 160 },
    ] as PackFeature[],
  },
  {
    id: 'coins-3500',
    coins: 3500,
    price: 19.99,
    originalPrice: 27.99,
    perCoin: '0.006',
    icon: Gem,
    gradient: 'from-eros-purple to-violet-500',
    glowColor: 'shadow-eros-purple/20',
    bgAccent: 'bg-eros-purple/10',
    borderAccent: 'border-eros-purple/30',
    features: [
      { type: 'chat', count: 700 },
      { type: 'image', count: 350 },
    ] as PackFeature[],
  },
  {
    id: 'coins-8000',
    coins: 8000,
    price: 39.99,
    originalPrice: 54.99,
    perCoin: '0.005',
    badge: 'BEST VALUE',
    icon: Zap,
    gradient: 'from-emerald-500 to-teal-400',
    glowColor: 'shadow-emerald-500/20',
    bgAccent: 'bg-emerald-500/10',
    borderAccent: 'border-emerald-500/30',
    features: [
      { type: 'chat', count: 1600 },
      { type: 'image', count: 800 },
    ] as PackFeature[],
  },
  {
    id: 'coins-18000',
    coins: 18000,
    price: 79.99,
    originalPrice: 109.99,
    perCoin: '0.004',
    badge: 'VIP',
    icon: Star,
    gradient: 'from-yellow-400 via-eros-pink to-eros-purple',
    glowColor: 'shadow-yellow-500/20',
    bgAccent: 'bg-gradient-to-br from-yellow-500/10 to-eros-purple/10',
    borderAccent: 'border-yellow-500/30',
    features: [
      { type: 'chat', count: 3600 },
      { type: 'image', count: 1800 },
    ] as PackFeature[],
  },
];

/**
 * Display-only mirror of applyPromoDiscount in
 * lib/server/billing/promo-codes.ts (incl. the €0.50 gateway floor) — the
 * checkout route recomputes the real charge server-side.
 */
function promoDiscountedPrice(price: number, percentOff: number): string {
  const discounted = Math.round(price * (1 - percentOff / 100) * 100) / 100;
  return Math.max(0.5, discounted).toFixed(2);
}

function badgeKey(badge: string): string {
  if (badge === 'POPULAR') return 'billing:coinModal.badge.popular';
  if (badge === 'BEST VALUE') return 'billing:coinModal.badge.bestValue';
  return 'billing:coinModal.badge.vip';
}

interface BuyCoinsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseComplete?: () => void;
}

export function BuyCoinsModal({
  open,
  onOpenChange,
  onPurchaseComplete,
}: BuyCoinsModalProps) {
  const [loadingPack, setLoadingPack] = useState<string | null>(null);
  const [mobileIndex, setMobileIndex] = useState(2); // Start at Popular
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const touchStartX = useRef(0);
  const offer = useFirstPurchaseOffer(open);
  const { t } = useTranslation('billing');

  // Funnel tracking: fires once per modal open (open is external state).
  useEffect(() => {
    if (open) trackFunnelEvent('buy_coins_opened');
  }, [open]);

  // Promo code entry. Validated via /api/billing/promo when applied; the
  // checkout route re-validates on purchase (server is the authority).
  const [promo, setPromo] = useState({
    input: '',
    applied: null as { code: string; percentOff: number } | null,
    checking: false,
    error: null as string | null,
  });

  const applyPromo = async () => {
    const code = promo.input.trim();
    if (!code) return;

    setPromo((p) => ({ ...p, checking: true, error: null }));

    try {
      const response = await fetch('/api/billing/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Validate against the cheapest pack; per-pack restrictions are
        // rare and re-checked at checkout anyway.
        body: JSON.stringify({ code, planId: COIN_PACKS[0]!.id }),
      });

      const data = await response.json();

      if (data.valid) {
        setPromo({
          input: '',
          applied: { code: data.code, percentOff: data.percentOff },
          checking: false,
          error: null,
        });
      } else {
        setPromo((p) => ({
          ...p,
          checking: false,
          error: t(`coinModal.promo.reasons.${data.reason ?? 'invalid'}`),
        }));
      }
    } catch {
      setPromo((p) => ({
        ...p,
        checking: false,
        error: t('coinModal.promo.reasons.invalid'),
      }));
    }
  };

  const handlePurchase = async (packId: string) => {
    setLoadingPack(packId);
    trackFunnelEvent('checkout_started', { packId });

    try {
      const response = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: packId,
          ...(promo.applied ? { promoCode: promo.applied.code } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.checkoutToken) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Keep the BuyCoinsModal Dialog open — the iframe renders inside
      // its content area (replacing the coin-pack grid), so the user
      // feels like the modal is morphing into the checkout instead of
      // a separate overlay popping up.
      setCheckoutToken(data.checkoutToken);
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoadingPack(null);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]!.clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0]!.clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && mobileIndex < COIN_PACKS.length - 1) {
        setMobileIndex((i) => i + 1);
      } else if (diff < 0 && mobileIndex > 0) {
        setMobileIndex((i) => i - 1);
      }
    }
  };

  const currentPack = COIN_PACKS[mobileIndex]!;

  // When the user clicks Buy on a pack, render the shared CheckoutOverlay
  // (same component used by /pricing) so the moment-of-payment UI is
  // identical across the app. Back returns to the coin-pack grid; the
  // in-flight CentroBill paymentPage URL is discarded — a fresh one will
  // be requested on the next Buy click.
  if (checkoutToken) {
    return (
      <CheckoutOverlay
        checkoutToken={checkoutToken}
        onBack={() => setCheckoutToken(null)}
        onComplete={() => {
          setCheckoutToken(null);
          onPurchaseComplete?.();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] overflow-visible border-0 bg-transparent p-0 shadow-none sm:max-w-2xl lg:max-w-4xl [&>button]:hidden">
        <div
          className="relative max-h-[90vh] overflow-x-hidden overflow-y-auto rounded-2xl shadow-[0_0_80px_rgba(168,85,247,0.15)] sm:rounded-3xl"
          style={{
            // Artwork is cropped to just inside its painted frame; this CSS
            // border replaces it so the frame always sits exactly on the
            // modal edge. 100% 100% stretches the art to the container.
            border: '1px solid rgba(255,158,210,0.55)',
            background:
              "url('/images/backgrounds/coins-popup-v2.avif') center / 100% 100% no-repeat, linear-gradient(180deg, #14081c 0%, #0a0510 100%)",
          }}
        >
          {/* Close button */}
          <button
            onClick={() => onOpenChange(false)}
            className="bg-foreground/5 hover:bg-foreground/10 absolute top-3 right-3 z-10 rounded-full p-1.5 transition-colors sm:top-4 sm:right-4"
          >
            <X className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          {/* Header */}
          <div className="relative px-4 pt-5 pb-3 text-center sm:px-6 sm:pt-6 sm:pb-4">
            <div className="to-eros-pink/20 mb-3 inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-gradient-to-r from-yellow-500/20 px-3 py-1">
              <Coins className="h-4 w-4 text-yellow-400" />
              <span className="text-xs font-semibold text-yellow-300 sm:text-sm">
                <Trans i18nKey="billing:coinModal.goldCoins" />
              </span>
            </div>
            <h2 className="text-foreground text-xl font-bold sm:text-2xl lg:text-3xl">
              <Trans i18nKey="billing:coinModal.heading" />
            </h2>
            <p className="text-muted-foreground mx-auto mt-1.5 max-w-md text-xs sm:text-sm">
              <Trans i18nKey="billing:coinModal.subheading" />
            </p>

            {/* First-purchase offer — coins doubled, live countdown */}
            {offer && (
              <div className="mx-auto mt-3 flex max-w-md items-center justify-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2">
                <Gift className="h-4 w-4 shrink-0 text-amber-400" />
                <span className="text-xs font-medium text-amber-100/90">
                  <Trans i18nKey="billing:coinModal.outOfCoins.firstPurchase.line" />
                </span>
                <span className="font-mono text-xs font-bold text-amber-300 tabular-nums">
                  {formatCountdown(offer.remainingMs)}
                </span>
              </div>
            )}

            {/* Promo code */}
            <div className="mx-auto mt-3 max-w-md">
              {promo.applied ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2">
                  <Tag className="h-4 w-4 shrink-0 text-emerald-400" />
                  <span className="text-xs font-medium text-emerald-200">
                    {t('coinModal.promo.applied', {
                      code: promo.applied.code,
                      percent: promo.applied.percentOff,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPromo({
                        input: '',
                        applied: null,
                        checking: false,
                        error: null,
                      })
                    }
                    className="hover:text-foreground text-emerald-300/70 transition-colors"
                    aria-label={t('coinModal.promo.remove')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="text"
                      value={promo.input}
                      onChange={(e) =>
                        setPromo((p) => ({
                          ...p,
                          input: e.target.value.toUpperCase(),
                          error: null,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void applyPromo();
                      }}
                      placeholder={t('coinModal.promo.placeholder')}
                      className="focus:border-eros-pink/50 border-foreground/10 bg-foreground/5 text-foreground placeholder:text-muted-foreground w-40 rounded-lg border px-3 py-1.5 text-xs uppercase focus:outline-none"
                      maxLength={32}
                      data-test="promo-code-input"
                    />
                    <button
                      type="button"
                      onClick={() => void applyPromo()}
                      disabled={promo.checking || !promo.input.trim()}
                      className="border-foreground/10 bg-foreground/5 text-foreground/80 hover:bg-foreground/10 hover:text-foreground rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      {promo.checking
                        ? t('coinModal.processing')
                        : t('coinModal.promo.apply')}
                    </button>
                  </div>
                  {promo.error && (
                    <p className="mt-1.5 text-[11px] text-red-400">
                      {promo.error}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* === MOBILE: Swipeable single card view === */}
          <div className="block px-3 pb-4 sm:hidden">
            <div
              className="relative"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Navigation arrows */}
              <button
                onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
                disabled={mobileIndex === 0}
                className="bg-foreground/10 absolute top-1/2 -left-1 z-10 -translate-y-1/2 rounded-full p-1.5 transition-opacity disabled:opacity-30"
              >
                <ChevronLeft className="text-foreground h-4 w-4" />
              </button>
              <button
                onClick={() =>
                  setMobileIndex((i) => Math.min(COIN_PACKS.length - 1, i + 1))
                }
                disabled={mobileIndex === COIN_PACKS.length - 1}
                className="bg-foreground/10 absolute top-1/2 -right-1 z-10 -translate-y-1/2 rounded-full p-1.5 transition-opacity disabled:opacity-30"
              >
                <ChevronRight className="text-foreground h-4 w-4" />
              </button>

              {/* Card */}
              <MobileCoinCard
                pack={currentPack}
                isLoading={loadingPack === currentPack.id}
                disabled={!!loadingPack}
                onPurchase={handlePurchase}
                promoPercentOff={promo.applied?.percentOff ?? null}
              />

              {/* Dot indicators */}
              <div className="mt-3 flex justify-center gap-1.5">
                {COIN_PACKS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setMobileIndex(i)}
                    className={cn(
                      'h-2 w-2 rounded-full transition-all',
                      i === mobileIndex
                        ? 'bg-eros-pink w-5'
                        : 'bg-foreground/20 hover:bg-foreground/40',
                    )}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* === DESKTOP: Scrollable grid === */}
          <div
            className="hidden max-h-[55vh] overflow-y-auto px-4 pb-4 sm:block sm:px-6"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.1) transparent',
            }}
          >
            <div className="grid grid-cols-2 gap-3 pt-3 lg:grid-cols-3">
              {COIN_PACKS.map((pack) => (
                <DesktopCoinCard
                  key={pack.id}
                  pack={pack}
                  isLoading={loadingPack === pack.id}
                  disabled={!!loadingPack}
                  onPurchase={handlePurchase}
                  promoPercentOff={promo.applied?.percentOff ?? null}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-foreground/5 relative flex items-center justify-center gap-2 border-t px-4 py-3 sm:px-6 sm:py-4">
            <Shield className="text-muted-foreground h-3.5 w-3.5" />
            <span className="text-muted-foreground text-[10px] sm:text-xs">
              <Trans i18nKey="billing:coinModal.footerNotice" />
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Desktop Card ----
function DesktopCoinCard({
  pack,
  isLoading,
  disabled,
  onPurchase,
  promoPercentOff,
}: {
  pack: (typeof COIN_PACKS)[0];
  isLoading: boolean;
  disabled: boolean;
  onPurchase: (id: string) => void;
  promoPercentOff: number | null;
}) {
  const { t } = useTranslation('billing');
  const { currency } = useDisplayCurrency();

  return (
    <div
      className={cn(
        'group relative cursor-pointer rounded-xl border p-3 transition-all lg:p-4',
        pack.highlighted
          ? `${pack.borderAccent} ${pack.bgAccent} shadow-lg ${pack.glowColor} ring-eros-pink/20 ring-1`
          : `border-foreground/10 bg-foreground/[0.03] hover:border-foreground/20 hover:bg-foreground/[0.05]`,
        disabled && !isLoading && 'pointer-events-none opacity-60',
      )}
      onClick={() => !disabled && onPurchase(pack.id)}
    >
      {/* Badge */}
      {pack.badge && (
        <div
          className={cn(
            'absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 rounded-full px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap text-white',
            pack.badge === 'POPULAR'
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r'
              : pack.badge === 'BEST VALUE'
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                : 'bg-gradient-to-r from-yellow-500 to-amber-500',
          )}
        >
          <Trans i18nKey={badgeKey(pack.badge)} />
        </div>
      )}

      {/* Icon + Coins */}
      <div className="mb-3 flex items-center gap-2.5">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`/images/coins/coins-${pack.coins}.avif`}
          alt={`${pack.coins} coins`}
          className="h-10 w-10 object-contain lg:h-11 lg:w-11"
          style={{ mixBlendMode: 'screen' }}
        />
        <div>
          <div className="text-foreground text-lg font-bold lg:text-xl">
            {pack.coins.toLocaleString()}
          </div>
          <div className="text-muted-foreground text-[10px] lg:text-xs">
            <Trans i18nKey="billing:coinModal.coins" />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {pack.features.map((f) => (
          <span
            key={`${f.type}-${f.count}`}
            className="bg-foreground/5 text-muted-foreground rounded-md px-1.5 py-0.5 text-[10px] lg:text-xs"
          >
            {t(
              f.type === 'chat'
                ? 'coinModal.features.chatUnlocks'
                : 'coinModal.features.imageUnlocks',
              { count: f.count },
            )}
          </span>
        ))}
      </div>

      {/* Price + Buy */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-baseline gap-1.5">
            {promoPercentOff ? (
              <>
                <span className="text-muted-foreground text-xs line-through lg:text-sm">
                  {formatPrice(pack.price, currency)}
                </span>
                <span className="text-xl font-bold text-emerald-300 lg:text-2xl">
                  {formatPrice(
                    promoDiscountedPrice(pack.price, promoPercentOff),
                    currency,
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="text-muted-foreground text-xs line-through lg:text-sm">
                  {formatPrice(pack.originalPrice, currency)}
                </span>
                <span className="text-foreground text-xl font-bold lg:text-2xl">
                  {formatPrice(pack.price, currency)}
                </span>
              </>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="text-muted-foreground text-[10px]">
              {t('coinModal.perCoin', {
                price: formatPrice(pack.perCoin, currency),
              })}
            </span>
            <span className="rounded-full bg-emerald-400/10 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
              {t('coinModal.save', {
                percent:
                  promoPercentOff ??
                  Math.round((1 - pack.price / pack.originalPrice) * 100),
              })}
            </span>
          </div>
        </div>
        <button
          disabled={disabled}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all',
            pack.highlighted
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white hover:opacity-90'
              : 'bg-foreground/10 text-foreground hover:bg-foreground/15',
            isLoading && 'opacity-70',
          )}
        >
          {isLoading ? (
            <span className="flex items-center gap-1.5">
              <span className="border-foreground/30 h-3 w-3 animate-spin rounded-full border-2 border-t-white" />
              <span>
                <Trans i18nKey="billing:coinModal.loading" />
              </span>
            </span>
          ) : (
            <Trans i18nKey="billing:coinModal.buy" />
          )}
        </button>
      </div>
    </div>
  );
}

// ---- Mobile Card ----
function MobileCoinCard({
  pack,
  isLoading,
  disabled,
  onPurchase,
  promoPercentOff,
}: {
  pack: (typeof COIN_PACKS)[0];
  isLoading: boolean;
  disabled: boolean;
  onPurchase: (id: string) => void;
  promoPercentOff: number | null;
}) {
  const { t } = useTranslation('billing');
  const { currency } = useDisplayCurrency();

  return (
    <div
      className={cn(
        'relative mx-4 rounded-2xl border p-4 transition-all',
        pack.highlighted
          ? `${pack.borderAccent} ${pack.bgAccent} shadow-lg ${pack.glowColor}`
          : 'border-foreground/10 bg-foreground/[0.03]',
      )}
    >
      {/* Badge */}
      {pack.badge && (
        <div
          className={cn(
            'absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 rounded-full px-3 py-0.5 text-[10px] font-bold text-white',
            pack.badge === 'POPULAR'
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r'
              : pack.badge === 'BEST VALUE'
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                : 'bg-gradient-to-r from-yellow-500 to-amber-500',
          )}
        >
          <Trans i18nKey={badgeKey(pack.badge)} />
        </div>
      )}

      {/* Icon + Amount */}
      <div className="mb-3 flex items-center justify-center gap-3">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`/images/coins/coins-${pack.coins}.avif`}
          alt={`${pack.coins} coins`}
          className="h-11 w-11 object-contain"
          style={{ mixBlendMode: 'screen' }}
        />
        <div className="text-center">
          <div className="text-foreground text-2xl font-bold">
            {pack.coins.toLocaleString()}
          </div>
          <div className="text-muted-foreground text-[11px]">
            <Trans i18nKey="billing:coinModal.goldCoins" />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="mb-3 flex justify-center gap-2">
        {pack.features.map((f) => (
          <span
            key={`${f.type}-${f.count}`}
            className="bg-foreground/5 text-muted-foreground rounded-lg px-2 py-0.5 text-[11px]"
          >
            {t(
              f.type === 'chat'
                ? 'coinModal.features.chatUnlocks'
                : 'coinModal.features.imageUnlocks',
              { count: f.count },
            )}
          </span>
        ))}
      </div>

      {/* Price */}
      <div className="mb-3 text-center">
        <div className="flex items-baseline justify-center gap-1.5">
          {promoPercentOff ? (
            <>
              <span className="text-muted-foreground text-sm line-through">
                {formatPrice(pack.price, currency)}
              </span>
              <span className="text-2xl font-bold text-emerald-300">
                {formatPrice(
                  promoDiscountedPrice(pack.price, promoPercentOff),
                  currency,
                )}
              </span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground text-sm line-through">
                {formatPrice(pack.originalPrice, currency)}
              </span>
              <span className="text-foreground text-2xl font-bold">
                {formatPrice(pack.price, currency)}
              </span>
            </>
          )}
          <span className="text-muted-foreground text-xs">{currency}</span>
        </div>
        <div className="mt-0.5 flex items-center justify-center gap-1.5">
          <span className="text-muted-foreground text-[11px]">
            {t('coinModal.perCoinFull', {
              price: formatPrice(pack.perCoin, currency),
            })}
          </span>
          <span className="rounded-full bg-emerald-400/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">
            {t('coinModal.save', {
              percent:
                promoPercentOff ??
                Math.round((1 - pack.price / pack.originalPrice) * 100),
            })}
          </span>
        </div>
      </div>

      {/* Buy Button */}
      <button
        onClick={() => !disabled && onPurchase(pack.id)}
        disabled={disabled}
        className={cn(
          'w-full rounded-xl py-2.5 text-sm font-semibold transition-all',
          pack.highlighted
            ? 'from-eros-pink to-eros-purple shadow-eros-pink/25 bg-gradient-to-r text-white shadow-lg hover:opacity-90'
            : 'bg-foreground/10 text-foreground hover:bg-foreground/15',
          isLoading && 'opacity-70',
        )}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="border-foreground/30 h-4 w-4 animate-spin rounded-full border-2 border-t-white" />
            <Trans i18nKey="billing:coinModal.processing" />
          </span>
        ) : (
          <Trans i18nKey="billing:coinModal.buyNow" />
        )}
      </button>
    </div>
  );
}

export default BuyCoinsModal;
